import React, { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormEventsAnalyticsTracker from 'components/analaytics/form-event-anayltics-tracker';
import useFormInteraction from 'helpers/hooks/useFormInteraction';
import { imageAmplienceToUrl } from 'helpers/utils/amplience/imageAmplienceToUrl';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { cn } from 'helpers/utils/classnames';
import CheckCircleOutline from 'icons/check-circle-outline';
import { useAccount } from 'frontastic';
import { newsletterValidationSchema } from './schema';
import { newsletterCopy, socialsGroup } from './types';
interface Props {
  newsLetterCopy: newsletterCopy;
  socialsGroup: socialsGroup[];
}

const FooterNewsletterSignup: FC<Props> = ({ newsLetterCopy, socialsGroup }) => {
  const [isSubmitted, setSubmitted] = useState<boolean>(false);
  const { handleStartInteraction, handleSubmitInteraction } = useFormInteraction('Signup Newsletter');
  const { signupNewsletter } = useAccount();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful, isSubmitting, isDirty },
    reset,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(newsletterValidationSchema),
    defaultValues: {
      email: '',
    },
  });

  if (isSubmitted) {
    setTimeout(() => {
      setSubmitted(false);
    }, 3000);
  }
  useEffect(() => {
    if (isDirty) {
      handleStartInteraction();
    }
  }, [isDirty]);

  return (
    <div className="flex flex-col items-center lg:mx-auto lg:max-w-[var(--page-max-width)] lg:flex-row lg:justify-between lg:px-4 lg:align-middle">
      <div className="lg:flex lg:flex-row lg:items-center lg:justify-start">
        <div className="flex flex-col justify-center text-center align-middle lg:mr-5 lg:text-left">
          <h2 className="mb-3 text-sm font-semibold lg:mb-1">{newsLetterCopy?.title}</h2>
          <h3 className="mb-9 text-center text-sm font-normal lg:mb-0 lg:text-start">{newsLetterCopy?.subtitle}</h3>
        </div>
        <form
          className="mx-4 mb-5 lg:mb-0 lg:mr-5"
          onSubmit={handleSubmit(async (data) => {
            await signupNewsletter(data.email);
            handleSubmitInteraction({ email: data.email, newsletterOptIn: true });
            setSubmitted(true);
            reset();
          })}
        >
          {errors && <FormEventsAnalyticsTracker formName="sqd" errors={errors} />}
          <div className="flex lg:max-h-[44px]">
            <input
              id="newsletterSignup"
              type="email"
              className={cn(
                'w-full rounded-bl-md rounded-tl-md border-0 border-transparent bg-white py-2 pl-4 pr-4 placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 lg:w-80',
                {
                  'border border-[#DA0F0F]': errors.email,
                },
              )}
              placeholder={'Enter email address'}
              aria-label="Newsletter Email"
              {...register('email')}
            />
            <button
              className={cn('rounded-br-md rounded-tr-md bg-primary-gray px-5 text-sm font-bold text-white', {
                'cursor-not-allowed': isSubmitting,
              })}
              type="submit"
              disabled={isSubmitting}
              onClick={() => {}}
              id="joinBtn"
              aria-label="Join - Signup Newsletter"
              data-testid="join-newsletter"
            >
              Join
            </button>
          </div>
          {errors.email && (
            <>
              <p className="mt-1 text-sm font-normal leading-[17px] text-[#DA0F0F]">{errors.email.message}</p>
            </>
          )}
          {!errors.email && isSubmitSuccessful && isSubmitted && (
            <p className="mt-1 flex items-center whitespace-nowrap text-sm font-normal leading-[17px] text-green-500">
              <CheckCircleOutline />
              You have successfully subscribed to our e-mails
            </p>
          )}
        </form>
      </div>
      <div className="flex flex-col items-center lg:flex lg:flex-row lg:justify-end">
        <h4 className="flex text-sm font-semibold lg:mr-5">#MyShadesOfLight</h4>
        <div className="mt-8 flex gap-11 lg:mt-0 lg:gap-5">
          {socialsGroup &&
            socialsGroup.map((social) => (
              <Link
                href={social?.link || ''}
                className="relative size-5"
                data-testid="socials-group-link"
                key={'socials-group-link-' + social.link}
                aria-label={social?.imageAlt}
              >
                <Image
                  src={imageAmplienceToUrl(social?.icon)}
                  alt={social?.imageAlt}
                  loader={() => amplienceImageLoader({ src: imageAmplienceToUrl(social?.icon), width: 20 })}
                  width={20}
                  height={20}
                  className="size-5 object-contain"
                />
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
};

export default FooterNewsletterSignup;
